/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadComboBoxOptions } from '@redux/modules/formGeneration';
import { createCondo, searchCondo, updateCondo } from '@redux/actionCreators/CondoCreator';
import Button from '@components/smartAdminUI/Button';
import Panel from '@components/smartAdminUI/Panel';
import CondoForm from '@components/form/forms/CondoManagementForm';

const submitTranslation = {
  Lookup_Property_Listing_CountyOrParish: 'CountyOrParish',
  Lookup_Property_Listing_City: 'City',
  Lookup_Property_Listing_SubdivisionName: 'SubdivisionName',
};
const lookupNames = { lookupNames: Object.keys(submitTranslation) };

// NOTE: Not all subdivisions will return a list -> will display no subdivisions message
// NOTE: Add Condo Name button disabled until a subdivision search has been performed

const CondoManagementPage = () => {
  const dispatch = useDispatch();

  const [editCondoName, setEditCondoName] = useState(''); // holds condo name to edit/update
  const [currCondo, setCurrCondo] = useState({}); // holds current condo record to edit
  const [newCondoName, setNewCondoName] = useState(''); // holds new condo name to create/insert
  const [subdivisionSelected, setSubdivisionSelected] = useState({}); // holds subdivision name from results list to create new condo record
  const [isEditable, setIsEditable] = useState(false); // flag to determine whether to display the condo name in a <span> or <input> for edit

  const condoSearchResults = useSelector(state => state.condo.condoSearchResults); // array of condo record objects
  const isSearching = useSelector(state => state.condo.isSearching);

  // API call to get combo box values on first render
  useEffect(() => {
    dispatch(loadComboBoxOptions(lookupNames));
  }, []);

  // submits search criteria to retrieve condo records
  const handleSubmit = event => {
    event.preventDefault();
    if (subdivisionSelected?.value) {
      const jsonBody = {
        whereClause: {
          clause: [{
            name: 'SubdivisionName',
            value: subdivisionSelected.value,
          }],
        },
      };
      dispatch(searchCondo(jsonBody));
      setIsEditable(false);
    }
  };

  // sets editable state to true and saves input as currCondo
  const handleEdit = condoItem => {
    setIsEditable(true);
    setCurrCondo(condoItem);
  };

  // stores input value to local state editCondoName
  const updateCondoName = e => {
    setEditCondoName(e.target.value);
  };

  // extract updated condo name values to submit to API / redux
  const buildUpdateWhereClause = values => {
    const whereClause = {
      clause: [],
    };
    const valueKeys = Object.keys(values);
    valueKeys.forEach(key => {
      if (values[key] && key === 'UniqueID') {
        whereClause.clause.push({
          name: 'UniqueID',
          value: values[key],
        });
      }
    });
    return whereClause;
  };

  // handles the condo name update and re rendering of updated results list (with small delay in re render)
  const handleSaveEdit = () => {
    setIsEditable(false);
    // currCondo is the original condo name record, where as editCondoName only holds the input string to update the condo name
    if (currCondo && editCondoName) {
      const updateClause = buildUpdateWhereClause(currCondo);
      const jsonBody = {
        whereClause: updateClause,
        CondoData: {
          CondoName: editCondoName, // holds new condo name value
          SubdivisionName: currCondo.SubdivisionName,
        },
      };
      dispatch(updateCondo(jsonBody));

      const searchClause = {
        clause: [{
          name: 'SubdivisionName',
          value: currCondo.SubdivisionName,
        }],
      };
      const updatedJsonBody = {
        whereClause: searchClause,
      };
      dispatch(searchCondo(updatedJsonBody)); // re render result list with edited condo name
      setEditCondoName(''); // clear edit name
    }
  };

  // reset editable state
  const handleCancelEdit = () => {
    setEditCondoName(''); // clear edit name
    setIsEditable(false);
  };

  // Captures the input's value and saves it to local state as newCondoName
  const handleInput = e => setNewCondoName(e.target.value);

  // Creates new subdivision and adds new record to results list for display
  const submitNewCondoName = e => {
    e.preventDefault();
    if ((condoSearchResults ?? [])) {
      if (newCondoName !== '' && subdivisionSelected !== '') {
        const newCondoRecord = {
          CondoData: {
            CondoName: newCondoName,
            SubdivisionName: subdivisionSelected.value,
          },
        };
        dispatch(createCondo(newCondoRecord));
      }
    }
  };

  // NOTE: Left as is for now as per Riz/Bill's instruction since delete might be added later
  const handleDelete = () => null;

  return (
    <>
      <div className="subheader">
        <h1 className="subheader-title">
          <i className="subheader-icon fal fa-building mr-2" />
          Condo Management
        </h1>
      </div>
      <div className="row">
        <div id="CondoManagementPanel" className="col-lg-6">
          <Panel header="Condo Management">
            <form onSubmit={handleSubmit}>
              <CondoForm setSubdivisionSelected={setSubdivisionSelected} />
              <Button variant="primary" type="submit">
                {isSearching
                  ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search mr-2" aria-hidden="true" />
                      Search
                    </>
                  )}
              </Button>
            </form>
          </Panel>
        </div>
        <div id="CondoNameResultsPanel" className="col-lg-6">
          <Panel header="Condo Name Results">
            <>
              <span className="condoLabel">
                New Condo Name
              </span>
              <label className="w-100" htmlFor="condoInput">
                <div className="condoInput input-group">
                  <input type="text" className="form-control" placeholder="Enter New Condo Name" value={newCondoName} onChange={handleInput} />
                  <div className="input-group-append">
                    <Button
                      disabled={!subdivisionSelected?.value} // disable button until a subdivisionName has been filled
                      variant="primary btn-sm"
                      onClick={submitNewCondoName}
                    >
                      Add Condo Name
                    </Button>
                  </div>
                </div>
              </label>
            </>
            <div
              id="slimscroll"
              className="custom-scroll"
              style={{ maxHeight: '70vh', overflow: 'auto' }}
            >
              <ul className="condoResultsList">
                {condoSearchResults
                  && condoSearchResults.sort((a, b) => a.CondoName.localeCompare(b.CondoName)).map(item => (
                    <div key={item.UniqueID}>
                      {(isEditable && (currCondo.CondoName === item.CondoName))
                        ? (
                          <div className="input-group" key={item.UniqueID}>
                            <input aria-label="Edit Condo Name" type="text" className="form-control" placeholder="" value={editCondoName} onChange={updateCondoName} />
                            <div className="input-group-append">
                              <Button className="btn-xs btn-icon" onClick={handleSaveEdit}>
                                <i className="fas fa-save" />
                              </Button>
                              <Button className="btn-xs btn-icon" onClick={handleCancelEdit}>
                                <i className="fas fa-close" />
                              </Button>
                            </div>
                          </div>
                        )
                        : (
                          <li key={item.UniqueID} id={item.UniqueID} className="condoListItems">
                            {`${item.CondoName}`}
                            <div className="condoIcons">
                              <Button className="btn-xs btn-icon" onClick={() => handleEdit(item)}><i className="far fa-pencil" /></Button>
                              <Button className="btn-xs btn-icon disabled" onClick={handleDelete}><i className="far fa-trash-alt" /></Button>
                            </div>
                          </li>
                        )}
                    </div>
                  ))}
              </ul>
              {(condoSearchResults && condoSearchResults.length === 0 && !isSearching)
                && <div>No condo names found</div>}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
};

export default CondoManagementPage;
